import { RoamingDictionary } from '@editor/roaming-dictionary';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import { lazyGetUserOptions } from 'owa-proofing-option';
import { getUserCulture } from 'owa-session-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getAccessTokenforResourceAsLazy, getDelegationTokenForOwa } from 'owa-tokenprovider';
import { trace } from 'owa-trace';

let dictionaryAutoSaveTimer: number | undefined = undefined;

const REFRESH_INTERVAL = 3600000; // 1 hour
const OWA_APP_ID = 122; // This ID is provided by the Roaming Service
const ROAMING_DICT_VERSION = '1.0.0';
const HOST_PLATFORM = 'Web';

export function initializeRoamingDictionary(proofingLocale: string): RoamingDictionary {
    trace.info('[roamingDictionary] Initialize roaming dictionary');
    return new RoamingDictionary(
        getAuthTokenForRoamingService,
        isFeatureEnabled('ring-dogfood') ? 'PPE' : 'PROD',
        OWA_APP_ID,
        ROAMING_DICT_VERSION,
        HOST_PLATFORM,
        proofingLocale,
        undefined
    );
}

export async function saveRoamingDictionaryInternal(
    roamingDictionary: RoamingDictionary
): Promise<boolean> {
    if (roamingDictionary) {
        const result = await roamingDictionary.saveWordListToRoamingService(getGuid());
        if (result.success) {
            return true;
        }
    }

    return false;
}

export function resetAutoSaveInterval(roamingDictionary: RoamingDictionary) {
    clearInterval(dictionaryAutoSaveTimer);
    initializeRoamingDictionaryAutoSaveTimer(roamingDictionary);
}

export function initializeRoamingDictionaryAutoSaveTimer(roamingDictionary: RoamingDictionary) {
    dictionaryAutoSaveTimer = window.setInterval(() => {
        if (roamingDictionary) {
            saveRoamingDictionaryInternal(roamingDictionary);
        }
    }, REFRESH_INTERVAL);
}

export async function getAuthTokenForRoamingService(): Promise<string> {
    const MSA_TOKEN_SCOPE = 'wl.skydrive'; // 'service::officeapps.live.com::MBI_SSL';
    const AAD_AUTH_ENDPOINT = 'https://officeapps.live.com';
    if (isConsumer()) {
        const token = await getDelegationTokenForOwa(MSA_TOKEN_SCOPE);
        return token || '';
    } else {
        let [token, tokenPromise] = getAccessTokenforResourceAsLazy(AAD_AUTH_ENDPOINT);
        if (!token) {
            token = (await tokenPromise) as string;
        }

        return (token as string) || '';
    }
}

export async function getProofingLocale(): Promise<string | undefined> {
    return new Promise<string | undefined>(resolve => {
        lazyGetUserOptions.importAndExecute(options => {
            if (options?.proofingLocale) {
                resolve(options.proofingLocale);
            } else {
                resolve(getUserCulture());
            }
        });
    });
}
